// src/pages/TradingDashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useUserContext } from '../../../layouts/UserContext';
import TradingChart from './TradingChart';
import RecommendationsList from './RecommendationsList';
import ActiveTradesList from './ActiveTradesList';
import TradingForm from './TradingForm';
import NotificationCenter from './NotificationCenter';
import StreamingRecommendations from './StreamingRecommendations';
import { 
  MarketDataPoint, 
  Notification, 
  TradeRecommendation, 
  ActiveTrade,
  TimeFrame,
  BotConfig
} from '../../../providers/tradingTypes';
import {
  getMarketData,
  updateMarketData,
  getActiveTrades,
  executeOrder,
  toggleTradingBot,
  updateBotConfig,
  getPreviousRecommendations
} from '../../../service/tradingService';

// Intervalo de actualización (en ms)
const MARKET_UPDATE_INTERVAL = 30000; // 30 segundos
const TRADES_UPDATE_INTERVAL = 60000; // 1 minuto

interface TradingDashboardProps {
  language: 'es' | 'en';
}

const TradingDashboard: React.FC<TradingDashboardProps> = ({ language }) => {
  const { user, isLoading, error } = useUserContext();
  
  // Estados
  const [marketData, setMarketData] = useState<MarketDataPoint[]>([]);
  const [recommendations, setRecommendations] = useState<TradeRecommendation[]>([]);
  const [activeTrades, setActiveTrades] = useState<ActiveTrade[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [symbols] = useState<string[]>(['BTC/USD', 'ETH/USD', 'XRP/USD', 'SOL/USD']);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('BTC/USD');
  const [timeframe, setTimeframe] = useState<TimeFrame>('1h');
  const [isAIBotActive, setIsAIBotActive] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isLoading_internal, setIsLoading_internal] = useState<boolean>(true);
  const [showStreaming, setShowStreaming] = useState<boolean>(false);
  
  // Configuración del bot
  const [botConfig, setBotConfig] = useState<BotConfig>({
    risk: 'medium',
    tradingPairs: ['BTC/USD', 'ETH/USD'],
    maxOrderSize: 1000
  });

  // Función para añadir notificaciones
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      id: Math.random().toString(36).substring(2, 9),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Mantener solo las últimas 50
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if (isLoading || error) return;
    
    const initializeDashboard = async () => {
      try {
        setIsLoading_internal(true);
        
        // Cargar datos del mercado
        const data = await getMarketData(selectedSymbol, timeframe);
        setMarketData(data);
        
        // Cargar operaciones activas
        const trades = await getActiveTrades();
        setActiveTrades(trades);
        
        // Cargar recomendaciones previas
        const prevRecommendations = await getPreviousRecommendations(selectedSymbol);
        setRecommendations(prevRecommendations);
        
        // Añadir notificación de conexión
        addNotification({
          type: 'success',
          message: 'Conectado al sistema de trading exitosamente'
        });
        
        setIsConnected(true);
        setLastUpdate(new Date());
        setIsLoading_internal(false);
      } catch (error) {
        console.error('Error al inicializar dashboard:', error);
        addNotification({
          type: 'error',
          message: 'Error al conectar con el sistema de trading'
        });
        setIsLoading_internal(false);
      }
    };
    
    initializeDashboard();
  }, [isLoading, error, selectedSymbol, timeframe, addNotification]);

  // Actualizar datos del mercado periódicamente
  useEffect(() => {
    if (!isConnected) return;
    
    const fetchMarketData = async () => {
      try {
        // Opción 1: Obtener datos nuevos completos
        // const data = await getMarketData(selectedSymbol, timeframe);
        
        // Opción 2: Actualizar solo el último punto (más eficiente)
        const updatedData = updateMarketData(selectedSymbol, timeframe);
        if (updatedData.length > 0) {
          setMarketData(updatedData);
          setLastUpdate(new Date());
        }
      } catch (error) {
        console.error('Error al actualizar datos del mercado:', error);
      }
    };
    
    // Actualización inicial
    fetchMarketData();
    
    // Configurar intervalo
    const interval = setInterval(fetchMarketData, MARKET_UPDATE_INTERVAL);
    
    return () => clearInterval(interval);
  }, [isConnected, selectedSymbol, timeframe]);

  // Actualizar operaciones activas periódicamente
  useEffect(() => {
    if (!isConnected) return;
    
    const fetchActiveTrades = async () => {
      try {
        const trades = await getActiveTrades();
        setActiveTrades(trades);
      } catch (error) {
        console.error('Error al actualizar operaciones activas:', error);
      }
    };
    
    const interval = setInterval(fetchActiveTrades, TRADES_UPDATE_INTERVAL);
    
    return () => clearInterval(interval);
  }, [isConnected]);

  // Manejar cambio de símbolo
  const handleSymbolChange = async (symbol: string) => {
    try {
      setIsLoading_internal(true);
      setSelectedSymbol(symbol);
      
      // Cargar datos del mercado para el nuevo símbolo
      const data = await getMarketData(symbol, timeframe);
      setMarketData(data);
      
      // Cargar recomendaciones para el nuevo símbolo
      const recommendations = await getPreviousRecommendations(symbol);
      setRecommendations(recommendations);
      
      setLastUpdate(new Date());
      setIsLoading_internal(false);
    } catch (error) {
      console.error('Error al cambiar símbolo:', error);
      addNotification({
        type: 'error',
        message: `Error al cargar datos para ${symbol}`
      });
      setIsLoading_internal(false);
    }
  };

  // Manejar cambio de timeframe
  const handleTimeframeChange = async (newTimeframe: TimeFrame) => {
    try {
      setIsLoading_internal(true);
      setTimeframe(newTimeframe);
      
      // Cargar datos del mercado con el nuevo timeframe
      const data = await getMarketData(selectedSymbol, newTimeframe);
      setMarketData(data);
      
      setLastUpdate(new Date());
      setIsLoading_internal(false);
    } catch (error) {
      console.error('Error al cambiar timeframe:', error);
      addNotification({
        type: 'error',
        message: `Error al cargar datos para timeframe ${newTimeframe}`
      });
      setIsLoading_internal(false);
    }
  };

  // Ejecutar orden de trading
  const handleExecuteOrder = async (orderData: any) => {
    try {
      setIsLoading_internal(true);
      
      // Ejecutar orden
      const response = await executeOrder({
        ...orderData,
        symbol: selectedSymbol
      });
      
      if (response.success) {
        // Añadir notificación de éxito
        addNotification({
          type: 'success',
          message: `Orden ejecutada: ${orderData.action} ${orderData.amount} ${selectedSymbol} a ${orderData.price}`
        });
        
        // Actualizar lista de operaciones activas
        const trades = await getActiveTrades();
        setActiveTrades(trades);
      } else {
        // Añadir notificación de error
        addNotification({
          type: 'error',
          message: `Error al ejecutar orden: ${response.message}`
        });
      }
      
      setIsLoading_internal(false);
    } catch (error) {
      console.error('Error al ejecutar orden:', error);
      addNotification({
        type: 'error',
        message: 'Error al enviar la orden'
      });
      setIsLoading_internal(false);
    }
  };

  // Activar/desactivar bot de IA
  const handleToggleBot = async (active: boolean) => {
    try {
      const response = await toggleTradingBot(active);
      
      if (response.success) {
        setIsAIBotActive(active);
        addNotification({
          type: 'info',
          message: active ? 'Bot de IA activado' : 'Bot de IA desactivado'
        });
      } else {
        addNotification({
          type: 'error',
          message: `Error al cambiar estado del bot: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Error al cambiar estado del bot:', error);
      addNotification({
        type: 'error',
        message: 'Error al cambiar estado del bot'
      });
    }
  };

  // Actualizar configuración del bot
  const handleUpdateBotConfig = async (config: BotConfig) => {
    try {
      const response = await updateBotConfig(config);
      
      if (response.success) {
        setBotConfig(config);
        addNotification({
          type: 'info',
          message: 'Configuración del bot actualizada'
        });
      } else {
        addNotification({
          type: 'error',
          message: `Error al actualizar configuración: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Error al actualizar configuración:', error);
      addNotification({
        type: 'error',
        message: 'Error al actualizar configuración del bot'
      });
    }
  };

  // Marcar notificación como leída
  const handleMarkNotificationAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  // Descartar notificación
  const handleDismissNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  // Ejecutar recomendación
  const handleExecuteRecommendation = (recommendation: TradeRecommendation) => {
    handleExecuteOrder({
      action: recommendation.action,
      price: recommendation.price,
      amount: 0.1, // Valor predeterminado que se puede ajustar
      stopLoss: recommendation.stopLoss,
      takeProfit: recommendation.takeProfit
    });
  };

  // Manejar nueva recomendación del streaming
  const handleNewStreamingRecommendation = (recommendation: TradeRecommendation) => {
    // Agregar al inicio de la lista de recomendaciones
    setRecommendations(prev => [recommendation, ...prev]);
    
    // Notificar al usuario
    addNotification({
      type: recommendation.confidence >= 80 ? 'warning' : 'info',
      message: `Nueva recomendación: ${recommendation.action} ${recommendation.symbol} a ${recommendation.price.toFixed(2)} (${recommendation.confidence}% confianza)`
    });
  };

  // Textos según idioma
  const texts = {
    title: language === 'es' ? 'Dashboard de Trading' : 'Trading Dashboard',
    status: language === 'es' ? 'Estado' : 'Status',
    connected: language === 'es' ? 'Conectado' : 'Connected',
    disconnected: language === 'es' ? 'Desconectado' : 'Disconnected',
    loading: language === 'es' ? 'Cargando...' : 'Loading...',
    bot: language === 'es' ? 'Bot IA' : 'AI Bot',
    lastUpdate: language === 'es' ? 'Última actualización' : 'Last update',
    chart: language === 'es' ? 'Gráfico de' : 'Chart for',
    activeTrades: language === 'es' ? 'Operaciones Activas' : 'Active Trades',
    newTrade: language === 'es' ? 'Nueva Operación' : 'New Trade',
    botRecommendations: language === 'es' ? 'Recomendaciones del Bot IA' : 'AI Bot Recommendations',
    notifications: language === 'es' ? 'Notificaciones' : 'Notifications',
    configureBot: language === 'es' ? 'Configurar Bot' : 'Configure Bot',
    aiConsult: language === 'es' ? 'Consultar IA' : 'Ask AI',
    hideAiConsult: language === 'es' ? 'Ocultar consulta' : 'Hide query'
  };

  // Renderizar componente
  return (
    <div className="trading-dashboard">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{texts.title}</h2>
        
        <div className="d-flex align-items-center">
          <div className="me-3">
            {texts.status}: {isConnected ? 
              <span className="text-success">{texts.connected}</span> : 
              <span className="text-danger">{texts.disconnected}</span>
            }
          </div>
          
          <div className="me-3">
            <select 
              className="form-select" 
              value={selectedSymbol} 
              onChange={(e) => handleSymbolChange(e.target.value)}
              disabled={isLoading_internal}
            >
              {symbols.map(symbol => (
                <option key={symbol} value={symbol}>{symbol}</option>
              ))}
            </select>
          </div>
          
          <div className="me-3">
            <select 
              className="form-select" 
              value={timeframe} 
              onChange={(e) => handleTimeframeChange(e.target.value as TimeFrame)}
              disabled={isLoading_internal}
            >
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="4h">4h</option>
              <option value="1d">1d</option>
            </select>
          </div>
          
          <div className="me-3">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setShowStreaming(!showStreaming)}
            >
              {showStreaming ? texts.hideAiConsult : texts.aiConsult}
            </button>
          </div>
          
          <div className="form-check form-switch">
            <input 
              className="form-check-input" 
              type="checkbox" 
              id="aiToggle" 
              checked={isAIBotActive}
              onChange={(e) => handleToggleBot(e.target.checked)}
              disabled={!isConnected || isLoading_internal}
            />
            <label className="form-check-label" htmlFor="aiToggle">
              {texts.bot}
            </label>
          </div>
        </div>
      </div>

      {isLoading_internal ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{texts.loading}</span>
          </div>
          <p className="mt-2">{texts.loading}</p>
        </div>
      ) : (
        <div className="row">
          <div className="col-md-8">
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between">
                <h5 className="mb-0">{texts.chart} {selectedSymbol}</h5>
                {lastUpdate && (
                  <small className="text-muted">
                    {texts.lastUpdate}: {lastUpdate.toLocaleTimeString()}
                  </small>
                )}
              </div>
              <div className="card-body">
                <TradingChart 
                  data={marketData} 
                  symbol={selectedSymbol} 
                />
              </div>
            </div>
            
            {/* Componente de Streaming (visible solo cuando se solicita) */}
            {showStreaming && (
              <div className="mb-4">
                <StreamingRecommendations
                  symbol={selectedSymbol}
                  timeframe={timeframe}
                  marketData={marketData}
                  onNewRecommendation={handleNewStreamingRecommendation}
                  language={language}
                  riskLevel={botConfig.risk}
                />
              </div>
            )}
            
            <div className="row">
              <div className="col-md-6">
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">{texts.activeTrades}</h5>
                  </div>
                  <div className="card-body">
                    <ActiveTradesList 
                      trades={activeTrades.filter(trade => trade.symbol === selectedSymbol) as any}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="mb-0">{texts.newTrade}</h5>
                  </div>
                  <div className="card-body">
                    <TradingForm 
                      onSubmit={handleExecuteOrder} 
                      symbol={selectedSymbol}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-4">
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{texts.botRecommendations}</h5>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                    // Modal para configurar el bot
                    // En una implementación real, aquí se abriría un modal
                    const newConfig = {
                      ...botConfig,
                      risk: (botConfig.risk === 'low' ? 'medium' : 
                            botConfig.risk === 'medium' ? 'high' : 'low') as 'low' | 'medium' | 'high'
                    };
                    handleUpdateBotConfig(newConfig);
                  }}
                >
                  {texts.configureBot}
                </button>
              </div>
              <div className="card-body">
                <RecommendationsList 
                  recommendations={recommendations} 
                  onExecute={handleExecuteRecommendation}
                />
              </div>
            </div>
            
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">{texts.notifications}</h5>
              </div>
              <div className="card-body">
                <NotificationCenter 
                  notifications={notifications}
                  onMarkAsRead={handleMarkNotificationAsRead}
                  onDismiss={handleDismissNotification}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingDashboard;